import React from 'react';
import styled from 'styled-components';
import { css } from '/imports/ui/css';

const TableStyled = styled.div`
  width: 100%;
  position: relative;
  overflow: auto;
  box-sizing: border-box;
  /* min-width:0 lets this shrink inside a flex parent; without it the container refuses to go below its
     content width and the PAGE scrolls sideways instead of the table. */
  min-width: 0;
  table {
    width: 100%;
    text-align: left;
    border-collapse: collapse;
    thead {
      th {
        position: sticky;
        top: 0;
        background: ${css.grey9};
        color: ${css.grey4};
        padding: 0.5rem 0.75rem;
        box-sizing: border-box;
        font-weight: 500;
        cursor: pointer;
        &:first-child {
          border-top-left-radius: ${css.borderRadius};
          border-bottom-left-radius: ${css.borderRadius};
        }
        &:last-child {
          border-top-right-radius: ${css.borderRadius};
          border-bottom-right-radius: ${css.borderRadius};
        }
        &.row-select {
          background: ${css.grey9};
          border-top: none;
          width: 0;
          padding: 0 0.75rem;
          text-align: center;
          z-index: 2;
        }
        &.row-actions {
          background: ${css.grey9};
          z-index: 2;
        }
        &.sort-asc {
          &::after {
            content: '▲';
            font-size: 0.65rem;
            opacity: 0.8;
            position: absolute;
            top: 0.5rem;
          }
        }
        &.sort-desc {
          &::after {
            content: '▼';
            font-size: 0.65rem;
            opacity: 0.8;
            position: absolute;
            top: 0.5rem;
          }
        }
      }
    }
    tbody {
      tr {
        border-bottom: 1px solid ${css.grey7};
      }
      td {
        padding: 0.75rem;
        box-sizing: border-box;
      }
      &.loading {
        opacity: 0.8;
      }
      .loading-flex {
        min-height: 10rem;
        max-height: 100%;
      }
      &.soft-loading {
        opacity: 0.8;
      }
    }
    .row-select {
      position: sticky;
      left: 0;
      padding: 0;
      width: 0;
      padding: 0 0.75rem;
      text-align: center;
      background: ${css.white};
      z-index: 1;
      input[type=checkbox] {
        vertical-align: middle;
      }
    }
    .row-actions {
      position: sticky;
      right: 0;
      padding: 0 0.75rem;
      width: 5rem;
      text-align: center;
      z-index: 1;
      a {
        margin-right: 0.25rem;
        cursor: pointer;
        &:last-child {
          margin-right: 0;
        }
        svg {
          width: 0.75rem;
          * {
            stroke: ${css.grey5};
          }
        }
      }
    }
  }
  .icon {
    svg {
      fill: ${css.grey4};
    }
  }
  .export-icon {
  }
  ${css.mobileMedia} {
    /* width:auto + min-width:100% is what turns "crush ten columns into 375px" into "scroll the table
       inside its own box": a wide table overflows and scrolls here, a narrow one still fills the width.
       Headers stay on one line, and data cells keep a floor so no column collapses to nothing — but
       not the checkbox/actions columns, which are meant to be narrow. */
    -webkit-overflow-scrolling: touch;
    table { width: auto; min-width: 100%; }
    thead th { white-space: nowrap; }
    tbody td:not(.row-select):not(.row-actions) { min-width: 6rem; }
  }
`;

interface TableProps {
  id?: string;
  children: React.ReactNode;
  onScrollBottom?(): void | Promise<void>;
}

let handlingScrollBottom = false;

/**
 * Scrollable table container that fires onScrollBottom when scrolled near the end.
 */
export const Table = (props: TableProps) => {
  /**
   * Detects when the scroll position nears the bottom and invokes onScrollBottom.
   * @param e - scroll event
   */
  const handleScroll = async (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target = e.target as HTMLElement;
    const hitBottom = target.scrollHeight - target.scrollTop - 0.1 * target.scrollHeight <= target.clientHeight;
    if (hitBottom && !handlingScrollBottom) {
      handlingScrollBottom = true;
      await props.onScrollBottom?.();
      handlingScrollBottom = false;
    }
  };
  return (
    <TableStyled id={props.id} className="table" onScroll={handleScroll}>
      {props.children}
    </TableStyled>
  );
};